import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatTimeDuration } from '@racemap/utilities/formattings/times';
import { formatDistance } from '@racemap/utilities/formattings/units';
import {
  eventHasVirtDuration,
  type Geofence,
  getVirtualRaceDistance,
  getVirtualRaceDuration,
  type PreparedRanksPackage,
  type PreparedStarterInfo,
  sortTimes,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import { Unit } from '@racemap/utilities/types/units';
import { Progress } from 'antd';
import type { Immutable } from 'immer';
import type { ReactNode } from 'react';
import { Center } from '../../../BasicComponents/MetaComponent';
import type { Column } from '../TableBody';

/** The starter's time or distance at the stage's finish geofence, null while they are still on course. */
function getStageResult(
  starterInfo: Immutable<PreparedStarterInfo> | undefined,
  timekeepings: Immutable<Array<Geofence>>,
): number | null {
  if (starterInfo == null || timekeepings.length === 0) return null;

  return starterInfo.results.at(timekeepings.length - 1)?.relativeProgress ?? null;
}

function StageProgressColComponent({
  starterInfo,
  stage,
  splits,
  timekeepings,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  stage: Immutable<RacemapEvent>;
  splits: Immutable<Splits>;
  timekeepings: Immutable<Array<Geofence>>;
}) {
  const eventVirtualRaceDistance = getVirtualRaceDistance(stage, splits);
  const eventVirtualRaceDuration = getVirtualRaceDuration(stage);
  const hasVirtDuration = eventHasVirtDuration(stage);
  const unitType = stage.playerOptions.unitType;
  const now =
    Math.floor(
      (starterInfo?.progress /
        (hasVirtDuration ? eventVirtualRaceDuration : eventVirtualRaceDistance)) *
        100000,
    ) / 1000;
  if (starterInfo.progress == null) return null;

  const stageResult = getStageResult(starterInfo, timekeepings);
  const progressValue = hasVirtDuration
    ? starterInfo.progress === -1
      ? '00:00:00'
      : formatTimeDuration(starterInfo.progress)
    : formatDistance({
        value: starterInfo.progress === -1 ? 0 : starterInfo.progress,
        inputUnit: Unit.Distance.m,
        unitType,
      });
  // A virtual-duration race finishes on a distance, a distance race on a time.
  const resultValue =
    stageResult == null
      ? null
      : hasVirtDuration
        ? formatDistance({ value: stageResult, inputUnit: Unit.Distance.m, unitType })
        : formatTimeDuration(stageResult);

  return (
    <Cell>
      <RankAndProgressValue>
        <div>{resultValue ?? progressValue}</div>
        <Rank>({starterInfo.rank})</Rank>
      </RankAndProgressValue>

      <ProgressContainer>
        {now > 0 && <Progress percent={now} status={now >= 100 ? 'success' : undefined} />}
      </ProgressContainer>
    </Cell>
  );
}

export function getStageProgressCol(
  stageEvent: Immutable<RacemapEvent>,
  splits: Immutable<Splits>,
  stageStarterData: Immutable<PreparedRanksPackage>,
): Column {
  const hasVirtDuration = eventHasVirtDuration(stageEvent);
  const timekeepings = stageStarterData.timekeepings;
  const starterResults = new Map(stageStarterData.starters.map((s) => [s.id, s]));

  return {
    key: `stage_progress-${stageEvent.id}`,
    title: <HeaderCell>{stageEvent.name}</HeaderCell>,
    width: 140,
    render: function renderer(row: Immutable<PreparedStarterInfo>): ReactNode {
      const starterInfo = starterResults.get(row.id);
      if (starterInfo == null) return '--';

      return (
        <StageProgressColComponent
          starterInfo={starterInfo}
          stage={stageEvent}
          splits={splits}
          timekeepings={timekeepings}
        />
      );
    },
    // Starters who finished the stage rank by their result, the rest by covered distance or duration.
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => {
      const resultA = getStageResult(starterResults.get(a.id), timekeepings);
      const resultB = getStageResult(starterResults.get(b.id), timekeepings);
      if (resultA != null && resultB != null) {
        return hasVirtDuration ? resultB - resultA : sortTimes(resultA, resultB);
      }
      if (resultA != null) return -1;
      if (resultB != null) return 1;

      const progressA = starterResults.get(a.id)?.progress || -1;
      const progressB = starterResults.get(b.id)?.progress || -1;

      return hasVirtDuration ? sortTimes(progressB, progressA) : progressB - progressA;
    },
  };
}

const Cell = styled(Center)`
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  width: 90%;
  height: 15px;
`;

const RankAndProgressValue = styled.div`
  display: flex;
  flex-direction: row;
`;

const Rank = styled.div`
  margin-left: 5px;
  color: ${RacemapColors.DarkGray};
`;

const HeaderCell = styled(Center)`
  width: 100%;
  font-weight: 600;
`;
